import { useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { streamChat } from '@/api/chatStream'
import { useChatStore } from '@/stores/chatStore'
import { useAuthStore, useTimerStore } from '@/stores'
import { taskKeys } from './useTasks'
import { timelogKeys } from './useTimeLogs'
import type { StreamEvent } from '@/types/streaming'
import type { ChatAction } from '@/api'

/**
 * Hook for streaming chat messages via SSE.
 */
export function useStreamChat() {
  const queryClient = useQueryClient()
  const {
    addMessage,
    appendToMessage,
    finalizeMessage,
    setToolIndicator,
    setSessionId,
    setLoading,
    sessionId,
  } = useChatStore()
  const { token } = useAuthStore()
  const { startTimer, stopTimer } = useTimerStore()
  const abortRef = useRef<AbortController | null>(null)
  const streamingMsgIdRef = useRef<string | null>(null)

  const handleActions = useCallback(
    (actions: ChatAction[]) => {
      for (const action of actions) {
        switch (action.type) {
          case 'task_created':
          case 'task_updated':
            queryClient.invalidateQueries({ queryKey: taskKeys.all })
            break
          case 'timer_started':
            if (action.data?.task_id && action.data?.task_title) {
              startTimer(
                action.data.task_id as string,
                action.data.task_title as string,
              )
            }
            break
          case 'timer_stopped':
            stopTimer()
            queryClient.invalidateQueries({ queryKey: timelogKeys.all })
            break
        }
      }
    },
    [queryClient, startTimer, stopTimer],
  )

  const handleStreamEvent = useCallback(
    (event: StreamEvent, msgId: string) => {
      switch (event.type) {
        case 'stream_start':
          if (event.session_id) {
            setSessionId(event.session_id)
          }
          break

        case 'token':
          if (event.content) {
            // Clear tool indicator once content starts flowing
            setToolIndicator(msgId, null)
            appendToMessage(msgId, event.content)
          }
          break

        case 'tool_call':
          if (event.tool_name) {
            setToolIndicator(msgId, event.tool_name)
          }
          break

        case 'tool_result':
          // Don't clear the indicator here — keep showing the last tool label
          // until content tokens arrive or the stream ends. This prevents
          // the indicator from flashing between sequential tool calls.
          break

        case 'stream_end':
          finalizeMessage(msgId)
          if (event.session_id) {
            setSessionId(event.session_id)
          }
          if (event.actions) {
            handleActions(event.actions as ChatAction[])
          }
          break

        case 'error':
          appendToMessage(
            msgId,
            event.message || 'Something went wrong. Try again?',
          )
          finalizeMessage(msgId)
          break
      }
    },
    [appendToMessage, finalizeMessage, setToolIndicator, setSessionId, handleActions],
  )

  const send = useCallback(
    async (message: string, context?: Record<string, unknown>) => {
      // Guard: abort any in-flight stream and finalize pending message
      if (abortRef.current) {
        abortRef.current.abort()
        abortRef.current = null
      }
      if (streamingMsgIdRef.current) {
        finalizeMessage(streamingMsgIdRef.current)
        streamingMsgIdRef.current = null
      }

      // Add user message immediately
      addMessage({ role: 'user', content: message })
      setLoading(true)

      // Create placeholder assistant message for streaming
      const msgId = addMessage({ role: 'assistant', content: '', isStreaming: true })
      streamingMsgIdRef.current = msgId

      const controller = new AbortController()
      abortRef.current = controller

      try {
        for await (const event of streamChat(
          message,
          sessionId,
          token || '',
          controller.signal,
          context,
        )) {
          if (!streamingMsgIdRef.current) break

          handleStreamEvent(event, streamingMsgIdRef.current)
        }
      } catch (err: unknown) {
        // On abort, just finalize
        if (err instanceof DOMException && err.name === 'AbortError') {
          if (streamingMsgIdRef.current) {
            finalizeMessage(streamingMsgIdRef.current)
          }
          setLoading(false)
          return
        }

        // On other errors, finalize with error content
        if (streamingMsgIdRef.current) {
          appendToMessage(
            streamingMsgIdRef.current,
            'Connection lost. Please try again.',
          )
          finalizeMessage(streamingMsgIdRef.current)
        }
        console.error('Stream failed:', err)
      } finally {
        // Safety net: if stream completed with empty content, show fallback.
        // Check BOTH still-streaming messages (stream never finished) AND
        // already-finalized messages with empty content (stream_end arrived
        // before any token events, e.g. tool-only responses).
        if (streamingMsgIdRef.current) {
          const msgs = useChatStore.getState().messages
          const streamingMsg = msgs.find((m) => m.id === streamingMsgIdRef.current)
          if (streamingMsg && !streamingMsg.content) {
            appendToMessage(
              streamingMsgIdRef.current,
              'No response received. Please try again.',
            )
          }
          // Ensure finalization if still streaming
          if (streamingMsg?.isStreaming) {
            finalizeMessage(streamingMsgIdRef.current)
          }
        }
        setLoading(false)
        abortRef.current = null
        streamingMsgIdRef.current = null
      }
    },
    [sessionId, token, addMessage, setLoading, finalizeMessage, appendToMessage, handleStreamEvent],
  )

  const cancel = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  return { send, cancel }
}
